import { Heart, Calendar, Receipt, Watch, Video, Activity, TrendingUp, Loader2 } from "lucide-react";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useAppointmentReminders } from "@/hooks/useAppointmentReminders";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, isToday, isTomorrow, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildTelehealthSessionPath } from "@/routes";

// Mock data for health metrics (vitals could be wired later)
const healthMetrics = [
  { label: "Heart Rate", value: "72", unit: "bpm", icon: Heart, status: "normal", trend: "+2" },
  { label: "Steps Today", value: "8,432", unit: "steps", icon: Activity, status: "good", trend: "+15%" },
  { label: "Sleep", value: "7.5", unit: "hours", icon: TrendingUp, status: "normal", trend: "-0.5" },
];

const connectedDevices = [
  { name: "Apple Watch Series 9", lastSync: "2 min ago", status: "connected" },
  { name: "Withings Blood Pressure", lastSync: "1 hour ago", status: "connected" },
];

function formatAppointmentDate(dateStr: string) {
  const d = parseISO(dateStr);
  if (isToday(d)) return "Today";
  if (isTomorrow(d)) return "Tomorrow";
  return format(d, "MMM d");
}

function formatTime(timeStr: string) {
  // timeStr is HH:mm:ss from DB
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

export default function MemberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useAppointmentReminders();

  const todayStr = new Date().toISOString().split("T")[0];

  // Upcoming appointments from DB
  const { data: upcomingAppointments = [], isLoading: loadingApts } = useQuery({
    queryKey: ["member-upcoming-appointments", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("id, scheduled_date, scheduled_time, visit_type, visit_reason, status, provider_id")
        .eq("member_id", user.id)
        .in("status", ["scheduled", "waiting", "in_progress"])
        .gte("scheduled_date", todayStr)
        .order("scheduled_date", { ascending: true })
        .order("scheduled_time", { ascending: true })
        .limit(5);
      if (error) throw error;
      const sessions = data ?? [];
      // Batch-fetch provider names
      const providerIds = [...new Set(sessions.map((s: any) => s.provider_id).filter(Boolean))];
      let providerMap: Record<string, string> = {};
      if (providerIds.length > 0) {
        const { data: providers } = await supabase
          .from("providers")
          .select("user_id, display_name")
          .in("user_id", providerIds);
        (providers || []).forEach((p: any) => {
          providerMap[p.user_id] = p.display_name;
        });
      }
      return sessions.map((s: any) => ({ ...s, provider_name: providerMap[s.provider_id] || null }));
    },
    enabled: !!user,
  });

  // Recent billing from DB (completed sessions with billing info)
  const { data: recentBilling = [], isLoading: loadingBilling } = useQuery({
    queryKey: ["member-recent-billing", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("id, visit_type, scheduled_date, billing_status, cpt_code, actual_duration_minutes")
        .eq("member_id", user.id)
        .eq("status", "completed")
        .order("scheduled_date", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  // Simple CPT to label/price map for display
  const cptInfo: Record<string, { label: string; price: number }> = {
    "99201": { label: "Office Visit - New", price: 75 },
    "99211": { label: "Office Visit - Established", price: 50 },
    "99421": { label: "Online Digital E/M", price: 40 },
    "99441": { label: "Telephone E/M", price: 55 },
    "99212": { label: "Office Visit", price: 75 },
    "99213": { label: "Office Visit (Moderate)", price: 125 },
    "99214": { label: "Office Visit (High)", price: 175 },
    "90837": { label: "Psychotherapy (60 min)", price: 200 },
    "99457": { label: "Remote Monitoring", price: 75 },
    "99458": { label: "Remote Monitoring Add-on", price: 50 },
  };

  const visitTypeLabels: Record<string, string> = {
    general: "General Consultation",
    behavioral_health: "Behavioral Health",
    follow_up: "Follow-Up Visit",
    prenatal: "Prenatal Visit",
    rpm: "Remote Patient Monitoring",
  };

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground">My Health Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              View your health data, appointments, and billing information.
            </p>
          </div>

          {/* Health Metrics */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {healthMetrics.map((metric) => (
              <Card key={metric.label} className="border-border/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{metric.label}</p>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-3xl font-bold text-foreground">{metric.value}</span>
                        <span className="text-sm text-muted-foreground">{metric.unit}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        <Badge variant={metric.status === "good" ? "default" : "secondary"} className="text-xs">
                          {metric.trend}
                        </Badge>
                        <span className="text-xs text-muted-foreground">vs last week</span>
                      </div>
                    </div>
                    <div className={`p-3 rounded-full ${metric.status === "good" ? "bg-success/10" : "bg-primary/10"}`}>
                      <metric.icon className={`h-6 w-6 ${metric.status === "good" ? "text-success" : "text-primary"}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Upcoming Appointments */}
            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Upcoming Appointments
                  </CardTitle>
                  <CardDescription>Your scheduled visits</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.MEMBER_APPOINTMENTS)}>View All</Button>
              </CardHeader>
              <CardContent>
                {loadingApts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : upcomingAppointments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No upcoming appointments.</p>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((apt: any) => {
                      const providerName = apt.provider_name ?? "Provider";
                      const typeLabel = visitTypeLabels[apt.visit_type] ?? apt.visit_type;
                      const isActionable = apt.status === "waiting" || apt.status === "in_progress";
                      return (
                        <div key={apt.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                          <div>
                            <p className="font-medium text-foreground">{providerName}</p>
                            <p className="text-sm text-muted-foreground">{typeLabel}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-primary font-medium">{formatAppointmentDate(apt.scheduled_date)}</span>
                              <span className="text-xs text-muted-foreground">at {formatTime(apt.scheduled_time)}</span>
                            </div>
                          </div>
                          {isActionable ? (
                            <Button size="sm" className="gradient-primary" onClick={() => navigate(buildTelehealthSessionPath(apt.id))}>
                              <Video className="h-4 w-4 mr-1" />
                              Join
                            </Button>
                          ) : (
                            <Badge variant="secondary">Scheduled</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <Button variant="outline" className="w-full mt-4" onClick={() => navigate(ROUTES.MEMBER_SCHEDULE)}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule New Appointment
                </Button>
              </CardContent>
            </Card>

            {/* Connected Devices */}
            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Watch className="h-5 w-5 text-primary" />
                    Connected Devices
                  </CardTitle>
                  <CardDescription>Your health monitoring devices</CardDescription>
                </div>
                <Button variant="outline" size="sm">Manage</Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {connectedDevices.map((device) => (
                    <div key={device.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-success/10">
                          <Watch className="h-5 w-5 text-success" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{device.name}</p>
                          <p className="text-xs text-muted-foreground">Last sync: {device.lastSync}</p>
                        </div>
                      </div>
                      <Badge variant="default" className="bg-success/10 text-success border-0">
                        Connected
                      </Badge>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-4">
                  <Activity className="h-4 w-4 mr-2" />
                  Connect New Device
                </Button>
              </CardContent>
            </Card>

            {/* Recent Billing */}
            <Card className="border-border/50 lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-primary" />
                    Recent Billing
                  </CardTitle>
                  <CardDescription>Your recent charges and payments</CardDescription>
                </div>
                <Button variant="outline" size="sm">View All</Button>
              </CardHeader>
              <CardContent>
                {loadingBilling ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : recentBilling.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No billing records yet.</p>
                ) : (
                  <div className="space-y-3">
                    {recentBilling.map((bill: any) => {
                      const info = bill.cpt_code ? cptInfo[bill.cpt_code] : null;
                      const serviceLabel = info?.label ?? visitTypeLabels[bill.visit_type] ?? "Consultation";
                      const amount = info?.price ?? 75;
                      const isPaid = bill.billing_status === "paid";
                      return (
                        <div key={bill.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                          <div>
                            <p className="font-medium text-foreground">{serviceLabel}</p>
                            <p className="text-sm text-muted-foreground">{format(parseISO(bill.scheduled_date), "MMM d, yyyy")}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-foreground">${amount.toFixed(2)}</span>
                            <Badge variant={isPaid ? "default" : "secondary"}>
                              {isPaid ? "Paid" : bill.billing_status === "denied" ? "Denied" : "Pending"}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
